import React, { useState } from "react";
import TaleButton from "@components/TaleButton";

const CompanySelection = ({
                              formData,
                              handleInputChange,
                              companies,
                              companiesLoading,
                              selectedCompany,
                              setSelectedCompany
                          }) => {
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [imageErrors, setImageErrors] = useState(new Set()); // Track images that failed to load

    // Filter companies based on search
    const filteredCompanies = companies?.filter(company =>
        company.id.toString().includes(searchTerm) ||
        company.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
        handleInputChange({ target: { name: "companyId", value: company.id } });
        setShowCompanyModal(false);
        setSearchTerm("");
    };

    const clearCompanySelection = () => {
        setSelectedCompany(null);
        handleInputChange({ target: { name: "companyId", value: "" } });
    };

    const getCompanyLogo = (company) => {
        const logoAsset = company.assets?.find(asset =>
            asset.entityType === "COMPANY" &&
            (asset.type === "IMAGE" || asset.description?.includes("logo"))
        );
        return logoAsset?.url || null;
    };

    const handleImageError = (companyId) => {
        // Add company ID to the set of failed images
        setImageErrors(prev => new Set(prev).add(companyId));
    };

    const handleImageLoad = (companyId) => {
        // Remove company ID from the set if image loads successfully
        setImageErrors(prev => {
            const newSet = new Set(prev);
            newSet.delete(companyId);
            return newSet;
        });
    };

    const CompanyLogo = ({ company, size = "md" }) => {
        const logoUrl = getCompanyLogo(company);
        const hasError = imageErrors.has(company.id);

        const sizeClasses = {
            sm: "w-12 h-12",
            md: "w-16 h-16",
            lg: "w-20 h-20"
        };

        const iconSize = {
            sm: "text-xl",
            md: "text-2xl",
            lg: "text-3xl"
        };

        if (hasError || !logoUrl) {
            return (
                <div className={`${sizeClasses[size]} rounded-lg border bg-gray-100 flex items-center justify-center`}>
                    <i className={`fas fa-building ${iconSize[size]} text-gray-400`}></i>
                </div>
            );
        }

        return (
            <img
                src={logoUrl}
                alt={`Logo ${company.companyName}`}
                className={`${sizeClasses[size]} rounded-lg object-cover border`}
                onError={() => handleImageError(company.id)}
                onLoad={() => handleImageLoad(company.id)}
            />
        );
    };

    const CompanyLogoSmall = ({ company }) => {
        const logoUrl = getCompanyLogo(company);
        const hasError = imageErrors.has(company.id);

        if (hasError || !logoUrl) {
            return (
                <div className="w-12 h-12 rounded-lg border bg-gray-100 flex items-center justify-center">
                    <i className="fas fa-building text-xl text-gray-400"></i>
                </div>
            );
        }

        return (
            <img
                src={logoUrl}
                alt={`Logo ${company.companyName}`}
                className="w-12 h-12 rounded-lg object-cover border"
                onError={() => handleImageError(company.id)}
                onLoad={() => handleImageLoad(company.id)}
            />
        );
    };

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Sélection de l'entreprise</h2>
            <div className="space-y-4">
                {/* Company Selection Input */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entreprise *
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={selectedCompany ? selectedCompany.companyName : ""}
                            readOnly
                            className="flex-1 px-3 py-2 border rounded-lg bg-gray-50"
                            placeholder="Cliquez pour sélectionner une entreprise"
                            onClick={() => setShowCompanyModal(true)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowCompanyModal(true)}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                            disabled={companiesLoading}
                        >
                            {companiesLoading ? (
                                <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                                <i className="fas fa-search"></i>
                            )}
                        </button>
                        {selectedCompany && (
                            <button
                                type="button"
                                onClick={clearCompanySelection}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                    </div>
                    <input
                        type="hidden"
                        name="companyId"
                        value={formData.companyId}
                        required
                    />
                </div>

                {/* Selected Company Info */}
                {selectedCompany && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-start gap-4">
                            {/* Company Logo */}
                            <div className="flex-shrink-0">
                                <CompanyLogo company={selectedCompany} size="md" />
                            </div>

                            {/* Company Info */}
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="font-bold text-lg">{selectedCompany.companyName}</h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                            {selectedCompany.industry || "Non spécifié"}
                                        </p>
                                    </div>
                                    {selectedCompany.numberOfEmployees && (
                                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                            {selectedCompany.numberOfEmployees} employés
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-3 mt-3">
                                    <div>
                                        <p className="text-sm text-gray-500">Email</p>
                                        <p className="font-medium">{selectedCompany.email || "Non renseigné"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Site web</p>
                                        <p className="font-medium">
                                            {selectedCompany.website ? (
                                                <a
                                                    href={`https://${selectedCompany.website}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    {selectedCompany.website}
                                                </a>
                                            ) : "Non renseigné"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Téléphone</p>
                                        <p className="font-medium">{selectedCompany.phone || "Non renseigné"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Chiffre d'affaires</p>
                                        <p className="font-medium">
                                            {selectedCompany.revenue ? (
                                                new Intl.NumberFormat('fr-TN', {
                                                    style: 'currency',
                                                    currency: 'TND',
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 0
                                                }).format(selectedCompany.revenue)
                                            ) : "Non renseigné"}
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-500">Adresse</p>
                                        <p className="font-medium">{selectedCompany.address || "Non renseigné"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Company Selection Modal */}
                {showCompanyModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
                            {/* Modal Header */}
                            <div className="p-6 border-b">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-xl font-bold">Sélectionner une entreprise</h3>
                                        <p className="text-gray-600 mt-1">
                                            {companies?.length || 0} entreprises disponibles
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowCompanyModal(false);
                                            setSearchTerm("");
                                        }}
                                        className="text-gray-500 hover:text-gray-700 text-2xl"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>

                                {/* Search Bar */}
                                <div className="mt-4">
                                    <div className="relative">
                                        <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                                        <input
                                            type="text"
                                            placeholder="Rechercher par nom, industrie ou email..."
                                            className="w-full pl-10 pr-4 py-3 border rounded-lg"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            autoFocus
                                        />
                                        {searchTerm && (
                                            <button
                                                onClick={() => setSearchTerm("")}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Body - Companies List */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {companiesLoading ? (
                                    <div className="flex justify-center items-center h-40">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : filteredCompanies.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-3">
                                            <i className="fas fa-building text-2xl text-gray-400"></i>
                                        </div>
                                        <p className="text-gray-500">
                                            {searchTerm ? "Aucune entreprise ne correspond à votre recherche" : "Aucune entreprise disponible"}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {filteredCompanies.map((company) => (
                                            <div
                                                key={company.id}
                                                className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                                                    selectedCompany?.id === company.id ? "border-blue-500 bg-blue-50" : ""
                                                }`}
                                                onClick={() => handleCompanySelect(company)}
                                            >
                                                <div className="flex items-start gap-3">
                                                    {/* Company Logo */}
                                                    <div className="flex-shrink-0">
                                                        <CompanyLogoSmall company={company} />
                                                    </div>

                                                    {/* Company Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-start">
                                                            <h4 className="font-bold text-sm truncate">
                                                                {company.companyName}
                                                            </h4>
                                                            {company.status === 1 && (
                                                                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                                                    Actif
                                                                </span>
                                                            )}
                                                        </div>

                                                        {company.industry && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                <i className="fas fa-industry mr-1"></i>
                                                                {company.industry}
                                                            </p>
                                                        )}

                                                        {company.email && (
                                                            <p className="text-xs text-gray-600 mt-1 truncate">
                                                                <i className="fas fa-envelope mr-1"></i>
                                                                {company.email}
                                                            </p>
                                                        )}

                                                        {company.numberOfEmployees && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                <i className="fas fa-users mr-1"></i>
                                                                {company.numberOfEmployees} employés
                                                            </p>
                                                        )}

                                                        {company.address && (
                                                            <p className="text-xs text-gray-600 mt-1 truncate">
                                                                <i className="fas fa-map-marker-alt mr-1"></i>
                                                                {company.address.split(',')[0]}
                                                            </p>
                                                        )}

                                                        <div className="flex items-center justify-between mt-3">
                                                            <span className="text-xs text-gray-500">
                                                                Créé le {new Date(company.createdAt).toLocaleDateString('fr-FR')}
                                                            </span>
                                                            {selectedCompany?.id === company.id && (
                                                                <span className="text-xs text-blue-600 font-semibold">
                                                                    <i className="fas fa-check mr-1"></i>
                                                                    Sélectionné
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="p-6 border-t bg-gray-50">
                                <div className="flex justify-between items-center">
                                    <div className="text-sm text-gray-600">
                                        {searchTerm ? (
                                            <span>{filteredCompanies.length} résultat(s) trouvé(s)</span>
                                        ) : (
                                            <span>{companies?.length || 0} entreprise(s) au total</span>
                                        )}
                                    </div>
                                    <div className="flex gap-3">
                                        <TaleButton
                                            type="button"
                                            variant="outlined"
                                            onClick={() => {
                                                setShowCompanyModal(false);
                                                setSearchTerm("");
                                            }}
                                        >
                                            Annuler
                                        </TaleButton>
                                        <TaleButton
                                            type="button"
                                            onClick={() => {
                                                if (filteredCompanies.length === 1) {
                                                    handleCompanySelect(filteredCompanies[0]);
                                                }
                                            }}
                                            disabled={filteredCompanies.length !== 1}
                                        >
                                            Sélectionner
                                        </TaleButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanySelection;
